import { entityClassFromSchema } from '@jupiterone/jupiter-web-toolkit/src';
import useBulkSynchronizationUpload from '@jupiterone/jupiter-web-toolkit/src/api/fetch/useBulkSynchronizationUpload';
import {
  Entity,
  EntityClass,
} from '@jupiterone/jupiter-web-toolkit/types/entity';
import { useEffect, useState } from 'react';
import uuid from 'uuid';
import { isIntegrationEntity } from '@jupiterone/jupiter-web-toolkit/src/components/drawers/EntityDrawer/utils/entity';

type UpdateEntityFunction = (
  timestamp: number,
  properties: any
) => Promise<false | Entity>;

export interface HookProps {
  onAddEntity?: (
    key: string,
    type: string[],
    className: string,
    timestamp: number,
    properties: any
  ) => Promise<void>;
  onUpdateEntity?: UpdateEntityFunction;
  mode: 'edit' | 'create';
  handleClose: () => void;
  entity?: Entity;
}

interface State {
  showClassPage: boolean;
  selectedClass: EntityClass | null;
  selectedClassName: string;
  isIntegrationEntity: boolean;
}

const initialState: State = {
  showClassPage: true,
  selectedClassName: '',
  selectedClass: null,
  isIntegrationEntity: false,
};

const useCreateEntityModal = ({
  mode,
  entity,
  handleClose,
  onAddEntity,
  onUpdateEntity,
}: Omit<HookProps, 'open'>) => {
  const [busy, setBusy] = useState<boolean>(false);
  const [state, setState] = useState<State>(initialState);
  const { bulkUpload } = useBulkSynchronizationUpload();

  useEffect(() => {
    if (mode === 'edit' && entity) {
      const integrationEntity = isIntegrationEntity(entity);
      const entityClass = entity.entityClass?.[0];
      const selectedClass = entityClass && entityClassFromSchema(entityClass);

      setState((prevState) => ({
        ...prevState,
        selectedClass: selectedClass || null,
        selectedClassName: entityClass || '',
        isIntegrationEntity: integrationEntity,
      }));
    }
  }, [mode, entity]);

  const closeModal = () => {
    resetState();
    handleClose();
  };

  const resetState = () => {
    setBusy(false);
    setState(initialState);
  };

  const handleSaveEntity = async (entity: Entity) => {
    setBusy(true);
    try {
      if (mode === 'create') {
        await addEntity(entity);
      } else {
        await updateEntity(entity);
      }
      closeModal();
    } catch (err) {
      setBusy(true);
    }
  };

  const addEntity = (entity: Entity) => {
    if (onAddEntity) {
      return onAddEntity(
        uuid(),
        entity.entityType,
        state.selectedClassName,
        new Date().getTime(),
        entity.properties
      );
    }
  };

  const updateEntity = async (entity: Entity) => {
    if (onUpdateEntity) {
      await onUpdateEntity(new Date().getTime(), entity.properties);
    }
  };

  const handleBulkUpload = async (uploadedFile: File, scope?: string) => {
    await bulkUpload({
      dataFile: uploadedFile,
      scope,
    });
    closeModal();
  };

  const handleSelectClass = async (
    selectedClass: EntityClass,
    selectedClassName: string
  ) => {
    setState({
      ...state,
      selectedClass,
      selectedClassName,
      showClassPage: false,
    });
  };

  const handleBack = () => {
    setState({
      ...state,
      selectedClass: null,
      showClassPage: true,
    });
  };
  return {
    state: {
      ...state,
      busy,
    },
    handleBack,
    closeModal,
    handleSaveEntity,
    handleSelectClass,
    handleBulkUpload,
  };
};
export default useCreateEntityModal;
